if (!customElements.get('product-bundles')) {
  customElements.define(
    'product-bundles',
    class ProductBundles extends HTMLElement {
      constructor() {
        super();
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.bundleItems = document.querySelectorAll('[data-bundle-data]');
        this.addToCartButton = document.querySelector('[data-add-to-cart-bundle]');
      }

      get variantId() {
        return this.dataset.id;
      }

      get price() {
        return Number(this.dataset.price);
      }

      get currency() {
        return this.dataset.currency;
      }

      get itemsInCart() {
        return Number(this.dataset.itemsInCart) || 0;
      }

      connectedCallback() {
        if (this.bundleItems.length <= 1) return;

        this.bundleItems.forEach((item) => {
          this.handleOptionChange(item);
        });

        if (!this.addToCartButton) return;
        this.addToCartButtonState();
        this.addToCartButton.addEventListener('click', () => this.handleAddToCart());

        this.prevQuantity = this.itemsInCart;
        this.handleRebuyCartChange = this.debounce((event) => {
          const items = event.detail?.cart?.cart?.items || [];
          const currentQuantity = items.filter((item) => item.variant_id == this.variantId).reduce((sum, item) => sum + item.quantity, 0);
          if (currentQuantity !== this.prevQuantity) {
            this.updateWidget();
          }

          if (this.prevQuantity > 0 && currentQuantity === 0) {
            this.updateWidget();
          }
          this.prevQuantity = currentQuantity;
        }, 100);

        document.addEventListener('rebuy:cart.change', this.handleRebuyCartChange);
      }

      disconnectedCallback() {
        document.removeEventListener('rebuy:cart.change', this.handleRebuyCartChange);
      }

      handleOptionChange(item) {
        const variantsData = JSON.parse(window.atob(item.dataset.bundleData));
        const options = item.querySelectorAll('[data-option]');
        if (options.length === 0) return;
        options.forEach((option) => {
          const value = option.dataset.option;
          const index = Number(option.dataset.index);
          option.addEventListener('click', () => {
            this.checkAvailability(variantsData, item, value, index);
            this.setActiveItem(item, value, index);
            this.handleImageChange(option);
            this.buildUrlForVariant(this.getActiveVariants());
            this.addToCartButtonState();
          });
        });
      }

      checkAvailability(variantsData, item, value, index) {
        const placeholderOption1 = index === 1 ? 'option_1' : 'option_2';
        const placeholderOption2 = index === 1 ? 'option_2' : 'option_1';
        const optionsIndex = index === 1 ? 2 : 1;
        const allSecondaryOptions = item.querySelectorAll(`[data-option][data-index="${optionsIndex}"]`);
        if (allSecondaryOptions.length > 0) {
          allSecondaryOptions.forEach((opt) => {
            opt.disabled = true;
            opt.classList.add(this.bindDisabledClasses(index));
          });
        }
        const currentOptionVariants = variantsData.filter((variant) => variant[placeholderOption1] === value);
        if (currentOptionVariants.length > 0) {
          currentOptionVariants.forEach((variant) => {
            const secondaryOption = item.querySelector(`[data-option="${variant[placeholderOption2]}"]`);
            if (secondaryOption) {
              secondaryOption.disabled = variant.quantity <= 0;
              secondaryOption.disabled ? secondaryOption.classList.add(this.bindDisabledClasses(index)) : secondaryOption.classList.remove(this.bindDisabledClasses(index));
            }
          });
        }
      }

      setActiveItem(item, value, index) {
        const options = item.querySelectorAll(`[data-option][data-index="${index}"]`);
        if (options.length === 0) return;
        options.forEach((option) => option.classList.remove(...this.bindActiveClasses(index)));

        const activeOption = item.querySelector(`[data-option="${value}"]`);
        if (!activeOption) return;
        activeOption.classList.add(...this.bindActiveClasses(index));
        const currentValueContainer = item.querySelector(`[ data-current-option="${index}"]`);
        if (!currentValueContainer) return;
        currentValueContainer.innerHTML = value;
      }

      buildUrlForVariant(variants) {
        if (variants.length <= 1) return;
        const variantIds = variants.map((variant) => variant.id);
        const url = `${location.origin}${location.pathname}?selected_variants=${variantIds.join(',')}`;
        history.replaceState({}, '', url);
      }

      async handleAddToCart() {
        const selectedVariants = this.getActiveVariants();
        if (selectedVariants.length === 0) return;
        const url = `${window.Shopify.routes.root}cart/add.js`;
        this.addToCartButton.disabled = true;
        this.addToCartButton.setAttribute('aria-disabled', true);
        this.addToCartButton.classList.add('loading');
        let sectionsToRender = '';
        if (this.cart) {
          sectionsToRender = this.cart.getSectionsToRender().map((section) => section.id);
        }
        const formData = {
          items: [
            {
              id: this.variantId,
              quantity: 1,
              properties: this.lineItemProperties(selectedVariants),
            },
          ],
          sections: sectionsToRender,
          sections_url: window.location.pathname,
        };

        const config = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        };

        try {
          const response = await fetch(url, config);
          const result = await response.json();

          if (result.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: this.variantId,
              errors: result.errors || result.description,
              message: result.message,
            });
          } else {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: this.variantId,
              cartData: result,
            });
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            this.updateWidget();
          }
        } catch(error) {
          console.error(error);
        } finally {
          this.addToCartButton.disabled = false;
          this.addToCartButton.removeAttribute('aria-disabled');
          this.addToCartButton.classList.remove('loading');
        }
      }

      lineItemProperties(selectedVariants) {
        let properties = {};
        selectedVariants.forEach((variant) => {
          const productTitle = variant.product_title;
          const variantTitle = variant.variant_title;
          properties[`${productTitle}`] = variantTitle;
        });
        properties._bundleOperation = this.prepareBundleOperation(selectedVariants);
        return properties;
      }

      prepareBundleOperation(selectedVariants) {
        let operations = [];
        const totalPrice = this.getTotalPrice(selectedVariants);

        selectedVariants.forEach(item => {
          const id = item.id;
          const price = item.price / totalPrice * this.price;
          operations.push({
            id: id,
            quantity: 1,
            price: price
          });
        });
        
        return operations;
      }

      async updateWidget() {
        const url = window.location.href;
        const response = await fetch(url);
        const result = await response.text();
        const parser = new DOMParser().parseFromString(result, 'text/html');
        const widgetContainer = document.querySelector('#product-bundles-container');
        const newWidget = parser.querySelector('#product-bundles-container');
        if (widgetContainer && newWidget) {
          widgetContainer.innerHTML = newWidget.innerHTML;
        }
        this.addToCartButtonState();
      }

      addToCartButtonState() {
        let state = [];
        const conditions = document.querySelectorAll('[data-bundle-data]');
        if (conditions.length === 0) return;
        conditions.forEach((condition) => {
          const option1 = condition.querySelector('[data-option][data-index="1"].active:not(:disabled)');
          const option2 = condition.querySelector('[data-option][data-index="2"].active:not(:disabled)');
          state.push(option1 && option2 ? true : false);
        });

        this.addToCartButton.disabled = state.includes(false);
      }

      getActiveVariants() {
        let variants = [];
        this.bundleItems.forEach((item) => {
          const variantsData = JSON.parse(window.atob(item.dataset.bundleData));
          const activeOption1 = item.querySelector('[data-option][data-index="1"].active:not(:disabled)');
          const activeOption2 = item.querySelector('[data-option][data-index="2"].active:not(:disabled)');
          if (!activeOption1 || !activeOption2) return;
          const selectedVariant = variantsData.find((variant) => variant.option_1 === activeOption1.dataset.option && variant.option_2 === activeOption2.dataset.option);
          if (!selectedVariant) return;
          variants.push(selectedVariant);
        });
        return variants;
      }

      handleImageChange(option) {
        const imageIndex = option.dataset.handleImage;
        const imageSrc = option.dataset.imageSrc;
        const bundleImage = document.querySelector(`[data-bundle-image-${imageIndex}] img`);
        if (!bundleImage) return;
        bundleImage.src = imageSrc;
      }

      bindDisabledClasses(index) {
        return index === 1 ? 'strike-through-diagonal' : 'color-disabled';
      }

      bindActiveClasses(index) {
        const colorClasses = ['active', 'shadow-[0px_0px_0px_5px_rgba(255,255,255,1),0px_0px_0px_6px_rgba(23,27,29,1)]'];
        const sizeClasses = ['active', 'bg-black', 'text-white'];
        return index === 1 ? colorClasses : sizeClasses;
      }

      getTotalPrice(selectedVariants) {
        let total = 0;
        selectedVariants.forEach(variant => total+= variant.price);

        return total;
      }

      debounce(fn, delay) {
        let t;
        return (...args) => {
          clearTimeout(t);
          t = setTimeout(() => fn.apply(this, args), delay);
        };
      }
    },
  );
}
